import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import {jwtDecode} from 'jwt-decode';

const LogoUploadPage = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [companyId, setCompanyId] = useState(null);

    useEffect(() => {
        try {
            const token = sessionStorage.getItem('authToken');
            if (!token) {
                setError("Niet ingelogd. Log eerst in.");
                return;
            }

            const decodedToken =jwtDecode(token);
            if (!decodedToken.companyId) {
                setError("Geen bedrijfs-ID gevonden in token.");
                return;
            }

            setCompanyId(decodedToken.companyId);
        } catch (err) {
            setError("Fout bij het ophalen van bedrijfs-ID: " + err.message);
        }
    }, []);

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError("Bestand is te groot. Maximum grootte is 5MB.");
                return;
            }

            if (!file.type.startsWith('image/')) {
                setError("Alleen afbeeldingen zijn toegestaan.");
                return;
            }

            setSelectedFile(file);
            setPreview(URL.createObjectURL(file));
            setError(null);
        }
    };

    const uploadLogo = async (event) => {
        event.preventDefault();
        if (!selectedFile) {
            setError("Selecteer eerst een bestand.");
            return;
        }

        if (!companyId) {
            setError("Geen bedrijfs-ID beschikbaar.");
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(false);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const token = sessionStorage.getItem('authToken');
            const response = await fetch(`http://localhost:4000/api/company-logos/upload/${companyId}`, {
                method: 'POST',
                body: formData,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Upload mislukt: ${response.statusText}`);
            }

            const data = await response.json();
            setSuccess(true);
            setSelectedFile(null);
            setPreview(null);

        } catch (err) {
            setError(`Er is een fout opgetreden: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    if (!companyId && !error) {
        return (
            <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-light p-0 m-0">
                <Row className="w-100">
                    <Col className="text-center">
                        <Alert variant="info" className="border-0 shadow-sm d-inline-block">
                            <div className="d-flex align-items-center">
                                <div className="spinner-border spinner-border-sm me-2" role="status">
                                    <span className="visually-hidden">Laden...</span>
                                </div>
                                Bedrijfs-ID wordt geladen...
                            </div>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center  p-0 m-0" style={{width:'94vw'}}>
            <Row className="w-100 justify-content-center">
                <Col xs={12} className="px-4">
                    <Card className="border-0 shadow-lg mx-auto" style={{ maxWidth: '1200px' }}>
                        <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
                            <h4 className="mb-0 fw-bold text-primary">Bedrijfslogo Uploaden</h4>
                            <hr className="mt-3" />
                        </Card.Header>
                        <Card.Body className="px-4 py-4">
                            {error && (
                                <Alert variant="danger" className="mb-4 border-0 shadow-sm">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert variant="success" className="mb-4 border-0 shadow-sm">
                                    <i className="bi bi-check-circle-fill me-2"></i>
                                    Logo is succesvol geüpload!
                                </Alert>
                            )}

                            <Row>
                                {/* Linker kolom met formulier */}
                                <Col md={6} className="border-end">
                                    <Form onSubmit={uploadLogo}>
                                        <Form.Group className="mb-4">
                                            <Form.Label className="fw-semibold mb-2">
                                                Selecteer een logo
                                            </Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileSelect}
                                                className="form-control-lg"
                                            />
                                            <Form.Text className="text-muted mt-2 d-block">
                                                <i className="bi bi-info-circle me-1"></i>
                                                Maximum bestandsgrootte: 5MB. Toegestane formaten: PNG, JPG, GIF
                                            </Form.Text>
                                        </Form.Group>

                                        <Button
                                            type="submit"
                                            variant="primary"
                                            disabled={loading || !selectedFile || !companyId}
                                            className="w-100 py-3 fw-semibold mt-4"
                                            size="lg"
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Bezig met uploaden...
                                                </>
                                            ) : (
                                                'Upload Logo'
                                            )}
                                        </Button>
                                    </Form>
                                </Col>

                                {/* Rechter kolom met preview */}
                                <Col md={6}>
                                    <div className="h-100 d-flex flex-column justify-content-center align-items-center">
                                        {preview ? (
                                            <div className="text-center">
                                                <h5 className="fw-semibold mb-3 text-secondary">Preview van uw logo</h5>
                                                <div className="bg-light p-4 rounded-3 shadow-sm" style={{ minHeight: '300px' }}>
                                                    <div className="d-flex justify-content-center align-items-center h-100">
                                                        <img
                                                            src={preview}
                                                            alt="Logo preview"
                                                            style={{
                                                                maxWidth: '100%',
                                                                maxHeight: '280px',
                                                                objectFit: 'contain'
                                                            }}
                                                            className="img-fluid"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center text-muted">
                                                <i className="bi bi-image fs-1 mb-2"></i>
                                                <p>Selecteer een afbeelding om de preview te zien</p>
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default LogoUploadPage;
